"use client";

import { useEffect, useRef, useState, type FC, type Ref } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Chip,
  Flex,
  Icon,
  Modal,
  ProgressBar,
  Text,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import {
  documentViewerStyle,
  documentViewerIFrameStyle,
  documentViewerIFrameContainerStyle,
  documentViewerContainerStyle,
} from "./DocumentViewer.css";

export interface DocumentViewerProps {
  viewEndpoint?: string;
  title?: string;
  documentTitle?: string;
  description?: string;
  acknowledgementLabel?: string;
  readLabel?: string;
  loading?: boolean;
  signed?: boolean;
  download?: boolean;
  signing?: boolean;
  onDownload?: () => void;
  onSign?: () => void;
}

/**
 * DocumentViewer component for full-screen document viewing with signing capabilities
 * Implements the same signing logic as DocumentCard but in a dedicated viewer layout
 */
export const DocumentViewer: FC<
  DocumentViewerProps & { ref?: Ref<HTMLDivElement> }
> = ({
  viewEndpoint,
  title,
  description,
  acknowledgementLabel,
  readLabel,
  loading = false,
  signed: signedProp = false,
  download,
  signing,
  onDownload,
  onSign,
  ref,
}) => {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSigned, setIsSigned] = useState(signedProp);

  const isSigningRef = useRef(false);

  const handleModalClose = () => {
    if ((loading || isChecked || isSigningRef.current) && isSigned === false) {
      return;
    }
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isChecked && !isSigningRef.current) {
      isSigningRef.current = true;
      onSign?.();
    }
  }, [isChecked, onSign]);

  useEffect(() => {
    if (signedProp) {
      setIsModalOpen(false);
      setIsSigned(true);
    }
  }, [signedProp]);

  return (
    <Flex ref={ref} direction="column" className={documentViewerStyle} grow>
      {loading && <ProgressBar variant="infinite" position="absolute" />}

      {viewEndpoint && (
        <div className={documentViewerIFrameContainerStyle}>
          <iframe
            className={mergeClasses(documentViewerIFrameStyle)}
            src={viewEndpoint}
            title={title}
          />
        </div>
      )}

      <Flex className={documentViewerContainerStyle}>
        <Flex direction="column" gap="s" grow>
          {isSigned && (
            <Chip
              variant="successSurface"
              size="l"
              leading={<Icon icon="check" size="s2" />}
              alignSelf="flex-start"
            >
              {t("documentViewer.signed")}
            </Chip>
          )}
          <Flex direction="column" gap="xs">
            {title && (
              <Text variant="bodyL" bold>
                {title}
              </Text>
            )}
            {description && (
              <Text color="currentColor50" markdown>
                {description}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex gap="s">
          {download && !signing && (
            <Button
              variant="primary"
              size="s"
              onClick={onDownload}
              alignSelf="flex-start"
              leading={<Icon icon="downloadFilled" size="s2" />}
            >
              {t("documentViewer.download")}
            </Button>
          )}

          {signing && !isSigned && (
            <Button
              variant="primary"
              size="s"
              onClick={() => setIsModalOpen(true)}
              alignSelf="flex-start"
              leading={<Icon icon="signature" size="s2" />}
            >
              {t("documentViewer.sign")}
            </Button>
          )}

          {signing && isSigned && (
            <Button
              variant="success"
              size="s"
              onClick={onDownload}
              alignSelf="flex-start"
              leading={<Icon icon="downloadFilled" size="s2" />}
            >
              {t("documentViewer.download")}
            </Button>
          )}
        </Flex>
      </Flex>

      {isModalOpen && (
        <Modal
          escape
          closeable
          onClose={handleModalClose}
          title={title}
          closeDisabled={(loading || isChecked) && isSigned === false}
          trailing={
            loading && <ProgressBar variant="infinite" position="absolute" />
          }
        >
          <Flex direction="column" gap="s">
            {readLabel && (
              <Checkbox
                checked={hasRead}
                onChange={() => setHasRead(!hasRead)}
                label={readLabel}
                disabled={isChecked}
              />
            )}
            {acknowledgementLabel && (
              <Checkbox
                checked={isChecked}
                onChange={() => setIsChecked(true)}
                disabled={isChecked || !hasRead}
                label={acknowledgementLabel}
              />
            )}
          </Flex>
        </Modal>
      )}
    </Flex>
  );
};
